import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCompaniesAggregated, type CompanyAggregate } from "../api";
import { formatDateEs, formatMoney } from "../lib/grouping";
import { pickBankBalance } from "./CompaniesAggregated";

/**
 * Vista imprimible del resumen por empresa: grid de 2 columnas donde cada
 * celda es una mini-tabla con las cuentas activas de la empresa (Banco,
 * últimos 4 dígitos del IBAN, saldo disponible, fecha del último sync).
 *
 * En pantalla se muestra con un botón "Imprimir". Al imprimir (window.print
 * o Ctrl+P), los estilos `@media print` ocultan la barra lateral / navegación
 * y dejan sólo el contenido con márgenes limpios.
 *
 * Se ordenan las empresas alfabéticamente por alias || name (empresas antes
 * que personas físicas). Solo empresas con al menos una cuenta activa.
 */
export function PrintSummary() {
  const [items, setItems] = useState<CompanyAggregate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchCompaniesAggregated();
        setItems(r.items);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const companies = useMemo(() => {
    if (!items) return [];
    return items
      .map((c) => ({
        ...c,
        activeAccounts: c.accounts.filter((a) => a.active),
      }))
      .filter((c) => c.activeAccounts.length > 0)
      .sort((a, b) => {
        if (a.isCompany !== b.isCompany) return a.isCompany ? -1 : 1;
        const an = (a.alias || a.name).toLowerCase();
        const bn = (b.alias || b.name).toLowerCase();
        return an.localeCompare(bn, "es");
      });
  }, [items]);

  const generatedAt = new Date().toLocaleString("es-ES");

  return (
    <>
      <style>{PRINT_CSS}</style>

      <section className="card no-print" style={{ marginBottom: 12 }}>
        <div className="card-head">
          <div>
            <h2>Resumen para imprimir</h2>
            <p className="meta">
              <Link to="/resumen">← Resumen por empresa</Link>
              {"  ·  "}
              Solo cuentas activas. Genera {companies.length} empresas · {" "}
              {companies.reduce((n, c) => n + c.activeAccounts.length, 0)} cuentas.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={() => window.print()} disabled={!items}>
              🖨 Imprimir
            </button>
          </div>
        </div>
        {error && <p className="error">{error}</p>}
        {items === null && !error && <p>Cargando…</p>}
      </section>

      <div className="print-area">
        <div className="print-header">
          <h1 style={{ margin: 0, fontSize: "1.15rem" }}>Resumen de saldos</h1>
          <div className="meta" style={{ fontSize: "0.85em" }}>
            Generado {generatedAt}
          </div>
        </div>

        <div className="print-grid">
          {companies.map((c) => (
            <CompanyCell key={c.id || c.name} company={c} />
          ))}
        </div>
      </div>
    </>
  );
}

function CompanyCell({
  company,
}: {
  company: CompanyAggregate & { activeAccounts: CompanyAggregate["accounts"] };
}) {
  const displayName = company.alias || company.name;
  // Saldo por moneda agregando solo las cuentas activas. Usamos `current`
  // (con fallback a `available`) porque en pólizas de crédito difieren:
  // current es el saldo dispuesto (lo que realmente se debe / se tiene) y
  // available es la línea de crédito libre. La contabilidad refleja current,
  // así que ese es el saldo económicamente correcto para el total.
  const totalsByCurrency = new Map<string, number>();
  for (const a of company.activeAccounts) {
    const bal = pickBankBalance(
      a.lastBalanceCurrent,
      a.lastBalanceAvailable,
      a.isCreditLine,
      a.lastMovementBalance,
    );
    if (bal === null) continue;
    const cur = a.currency || "EUR";
    totalsByCurrency.set(cur, (totalsByCurrency.get(cur) ?? 0) + bal);
  }
  const totals = Array.from(totalsByCurrency.entries());
  return (
    <div className="print-cell">
      <div className="print-cell-title">
        <span>{displayName}</span>
        {totals.length > 0 && (
          <span className="print-cell-total">
            {totals.map(([cur, sum]) => formatMoney(sum, cur)).join(" · ")}
          </span>
        )}
      </div>
      <table className="print-table">
        <thead>
          <tr>
            <th>Banco</th>
            <th>Últimos 4</th>
            <th className="num">Saldo</th>
            <th className="num">Saldo SAP</th>
            <th>Estado</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {company.activeAccounts.map((a) => {
            const bankBal = pickBankBalance(
              a.lastBalanceCurrent,
              a.lastBalanceAvailable,
              a.isCreditLine,
              a.lastMovementBalance,
            );
            const sapBal = a.sapBalance;
            const isPoliza = a.isCreditLine;
            const polizaBalance = isPoliza
              ? (a.lastBalanceCurrent !== null && a.lastBalanceCurrent !== 0
                  ? a.lastBalanceCurrent
                  : a.lastMovementBalance)
              : null;
            let status: string;
            let statusClass = "print-status-neutral";
            if (sapBal === null) {
              status = "sin SAP";
            } else if (bankBal === null) {
              status = "sin banco";
            } else if (Math.abs(bankBal - sapBal) < 0.01) {
              status = "Cuadrado ✓";
              statusClass = "print-status-ok";
            } else if (a.lastReconciledDate) {
              status = `hasta ${formatDateEs(a.lastReconciledDate)}`;
              statusClass = "print-status-warn";
            } else {
              status = "Descuadrado";
              statusClass = "print-status-warn";
            }
            return (
              <tr key={a.iban}>
                <td>{a.bankName || a.bankCode}</td>
                <td>
                  <code>{a.iban.slice(-4)}</code>
                </td>
                <td className="num">
                  {isPoliza ? (
                    polizaBalance !== null ? (
                      <>
                        {formatMoney(polizaBalance, a.currency)}
                        {a.lastBalanceAvailable !== null && (
                          <div className="print-current-aside">
                            disp. {formatMoney(a.lastBalanceAvailable, a.currency)}
                          </div>
                        )}
                      </>
                    ) : (
                      "— refresh"
                    )
                  ) : a.lastBalanceAvailable !== null ? (
                    formatMoney(a.lastBalanceAvailable, a.currency)
                  ) : (
                    "—"
                  )}
                </td>
                <td className="num">
                  {sapBal !== null ? formatMoney(sapBal, a.currency) : "—"}
                </td>
                <td className={statusClass}>{status}</td>
                <td>
                  {a.lastSyncedAt
                    ? new Date(a.lastSyncedAt).toLocaleDateString("es-ES")
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * CSS específico de esta vista. Se inyecta como <style> inline para no
 * contaminar la hoja de estilos global.
 *
 * `@media print` — regla clave: la app está montada dentro de `.shell` (con
 * sidebar). Al imprimir queremos que solo se vea `.print-area` a página
 * completa. Ocultamos la sidebar y la cabecera de esta propia vista.
 */
const PRINT_CSS = `
.print-area {
  padding: 8px 12px;
  color: var(--ink-900);
}
.print-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
  border-bottom: 2px solid var(--brand-700);
  padding-bottom: 6px;
}
.print-header h1 {
  color: var(--brand-800);
}
.print-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.print-cell {
  border: 1px solid var(--brand-700);
  border-radius: 4px;
  padding: 6px 8px;
  page-break-inside: avoid;
  break-inside: avoid;
  background: #ffffff;
}
.print-cell-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  font-weight: 700;
  padding: 2px 4px 4px 4px;
  border-bottom: 1px solid var(--brand-700);
  margin-bottom: 4px;
  color: var(--brand-800);
}
.print-cell-total {
  font-weight: 700;
  color: var(--brand-700);
  white-space: nowrap;
}
.print-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85em;
}
.print-table th,
.print-table td {
  border: 1px solid var(--brand-500);
  padding: 3px 5px;
  text-align: left;
}
.print-table th {
  background: var(--brand-050);
  color: var(--brand-800);
  font-weight: 600;
}
.print-table td.num,
.print-table th.num {
  text-align: right;
  white-space: nowrap;
}
.print-table code {
  font-family: inherit;
  color: var(--ink-900);
}
.print-current-aside {
  font-size: 0.85em;
  color: #666;
  margin-top: 1px;
}
.print-status-ok {
  color: #2e7d32;
  font-weight: 600;
  white-space: nowrap;
}
.print-status-warn {
  color: #b26a00;
  font-weight: 600;
  white-space: nowrap;
}
.print-status-neutral {
  color: #666;
  white-space: nowrap;
}

@media print {
  @page {
    size: A4 portrait;
    margin: 6mm;
  }
  /* Clave para que los navegadores respeten fondos y colores en la impresion
     por defecto los eliminan como "ahorro de tinta". */
  html, body,
  .print-area,
  .print-header,
  .print-cell,
  .print-cell-title,
  .print-table,
  .print-table th,
  .print-table td {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  .shell,
  .shell > .main {
    display: block !important;
    padding: 0 !important;
    margin: 0 !important;
    background: #ffffff;
  }
  .sidebar,
  .no-print {
    display: none !important;
  }
  .print-area {
    padding: 0;
    font-size: 8pt;
  }
  .print-header {
    margin-bottom: 4px;
    padding-bottom: 2px;
  }
  .print-header h1 {
    font-size: 10pt !important;
  }
  .print-header .meta {
    font-size: 7pt !important;
  }
  .print-grid {
    gap: 3mm;
  }
  .print-cell {
    padding: 2px 4px;
  }
  .print-cell-title {
    padding: 1px 0 2px 0;
    margin-bottom: 2px;
    font-size: 8.5pt;
  }
  .print-table {
    font-size: 7pt;
  }
  .print-table th,
  .print-table td {
    padding: 1px 3px;
  }
}
`;
